import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDRESummary } from '@/hooks/useFinancialData';
import { formatBRL, formatDelta } from '@/lib/format';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';

interface Row {
  label: string;
  value: number;
  emphasis?: boolean;
  negative?: boolean;
  indent?: boolean;
}

export function DREsummary() {
  const { data, loading } = useDRESummary();

  if (loading) {
    return (
      <div className="card p-5 h-full">
        <SectionHeader title="DRE — mês corrente" subtitle="Carregando…" />
        <div className="space-y-2 mt-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-8 rounded bg-surface-subtle animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || (data.revenue_gross_cents === 0 && data.expense_cents === 0)) {
    return (
      <div className="card p-5 h-full">
        <SectionHeader title="DRE — mês corrente" subtitle="Sem dados" />
        <EmptyState
          icon={FileText}
          title="Sem movimentações no mês"
          description="Importe seu extrato para ver o resultado financeiro do mês."
        />
      </div>
    );
  }

  const rows: Row[] = [
    { label: 'Receita bruta', value: data.revenue_gross_cents, emphasis: true },
    { label: '(−) Despesas', value: -data.expense_cents, indent: true, negative: true },
    { label: 'Resultado líquido', value: data.net_income_cents, emphasis: true },
  ];

  const delta = formatDelta(data.net_income_cents, data.prev_net_income_cents);

  return (
    <div className="card p-5 h-full">
      <SectionHeader
        title="DRE — mês corrente"
        subtitle={`Resultado: ${formatBRL(data.net_income_cents)} (${delta.label} vs mês anterior)`}
      />
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={r.emphasis ? 'bg-surface-subtle' : ''}>
                <td className={`px-4 py-2.5 ${r.indent ? 'pl-7' : ''} ${r.emphasis ? 'font-semibold text-ink' : 'text-ink-muted'}`}>
                  {r.label}
                </td>
                <td
                  className={`px-4 py-2.5 text-right tabular-nums font-medium ${
                    r.emphasis ? 'text-ink font-semibold' : r.negative ? 'text-negative' : 'text-ink'
                  }`}
                >
                  {formatBRL(r.value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-ink-soft">
          Categorias e COGS disponíveis após classificar as transações.
        </p>
        <Link to="/financeiro/dre" className="text-xs text-brand-700 hover:underline font-medium">
          Ver DRE detalhada →
        </Link>
      </div>
    </div>
  );
}
